// Só o TIPO do template: o módulo de hooks puxa o cliente HTTP, que este arquivo (e o teste dele) não precisa.
import type { ITemplateEvento } from '../../../../hooks/useEventoContrato';

/**
 * Tipo de contrato: o jurídico usa um modelo por natureza do fornecimento (serviço, compra
 * de produto, parceria de alimentos e bebidas, operação de bar, venda de fotos). Cada
 * template do evento é de um tipo e o contrato gera só com os templates do tipo dele.
 * O catálogo vem do back; aqui só o padrão e as funções de apoio da tela.
 */
export interface ITipoContrato {
  valor: string;
  rotulo: string;
  /** Quantos templates deste tipo o evento tem. */
  arquivos: number;
  /** Tem template e todos estão confirmados: o contrato deste tipo pode ser gerado. */
  confirmado: boolean;
}

export const TIPO_CONTRATO_PADRAO = 'servico';

/**
 * Termo aditivo: o robô de eventos preenche o modelo do jurídico a cada aditivo. O template
 * deste tipo só guarda o arquivo — o back recusa a leitura e o "Gerar" (não há mapa a confirmar).
 */
export const TIPO_CONTRATO_ADITIVO = 'aditivo';

export function rotuloDoTipo(tipos: ITipoContrato[] | undefined, valor?: string | null): string {
  return tipos?.find(t => t.valor === valor)?.rotulo ?? valor ?? '';
}

/** O template do tipo no evento, em palavras ('' quando está pronto para gerar). */
export function estadoDoTipo(t?: ITipoContrato): string {
  if (!t) return '';
  if (!t.arquivos) return 'sem template no evento';
  return t.confirmado ? '' : 'template sem confirmar';
}

/** O template com o tipo garantido (`comTipo`). */
export type ITemplateComTipo = ITemplateEvento & { contrato_tipo: string };

export interface IGrupoDoTipo {
  valor: string;
  /** O tipo no catálogo (rótulo e estado); ausente se o template tem um tipo que o catálogo não conhece. */
  tipo?: ITipoContrato;
  arquivos: ITemplateComTipo[];
}

/** Template sem tipo (API anterior ao tipo de contrato) é de serviço — o único modelo que existia. */
export function comTipo(templates: ITemplateEvento[]): ITemplateComTipo[] {
  return templates.map(t => ({ ...t, contrato_tipo: t.contrato_tipo || TIPO_CONTRATO_PADRAO }));
}

/**
 * Os templates do evento em blocos por tipo de contrato: na ordem do catálogo, só os tipos
 * que têm arquivo; tipo fora do catálogo vai no fim, para não sumir da tela.
 */
export function agruparPorTipo(
  templates: ITemplateComTipo[],
  tipos: ITipoContrato[],
): IGrupoDoTipo[] {
  const valores = [
    ...tipos.map(t => t.valor),
    ...templates.map(t => t.contrato_tipo).filter(v => !tipos.some(t => t.valor === v)),
  ].filter((v, i, todos) => todos.indexOf(v) === i);
  return valores
    .map(valor => ({
      valor,
      tipo: tipos.find(t => t.valor === valor),
      arquivos: templates.filter(t => t.contrato_tipo === valor),
    }))
    .filter(s => s.arquivos.length > 0);
}
